import type { AxiosInstance } from "axios";
import type { TranslateFn, U2000ApiErrorResult } from "../u2000Core/axios/U2000HandleApiError";
import type { U2000SearchPayload, U2000SearchParameter } from "../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000GenerateUrlUUID } from "../u2000Core/tools/U2000UrlHelpers";
import type { U3120CustomerContact } from "../u3120Customer/U3120Service";
import { emptyCustomerContact } from "../u3120Customer/U3120Service";
import type { U3134TransactionKind, U3134WorkInProgressSearchRow } from "../u3134WorkInProgress/U3134Service";
import { normalizeU3125WipTransactionLine } from "../u3125Facture/U3125Service";
import type { U3121GoogleCalendarDateDivergence } from "../u2000Core/types/U2000WebSocketMessages";

export { U3120ContactPhoneType } from "../u3120Customer/U3120Service";
export type {
  U3121GoogleCalendarDateDivergence,
  U3121GoogleCalendarDateDivergenceKind,
} from "../u2000Core/types/U2000WebSocketMessages";

const LOGICALUNIT_URL = "U3121";

type LogicalUnitEndpointsType = {
  INITLU: string;
  SEARCH: string;
  GET: (id: string | number) => string;
  CREATE: string;
  UPDATE: (id: string | number) => string;
  DELETE: (id: string | number) => string;
  AUDITS: (id: string | number) => string;
  FINDEMPLOYEE: string;
  FINDPROJECTMANAGER: string;
  FINDLANDSURVEYOR: string;
  FINDCUSTOMER: string;
  FINDPROJECT: string;
  FINDBILLINGPROFILES: string;
  FINDTASKASSIGNMENTS: string;
  FINDTASKS: string;
  GETSUBPROJECT: (projectId: string | number, subProjectId: string | number) => string;
  GETSUBPROJECTAUDITS: (projectId: string | number, subProjectId: string | number) => string;
  CREATESUBPROJECT: (projectId: string | number) => string;
  UPDATESUBPROJECT: (projectId: string | number, subProjectId: string | number) => string;
  DELETESUBPROJECT: (projectId: string | number, subProjectId: string | number) => string;
  CHANGESTATUS: (id: string | number) => string;
  CHANGESTATUSSUBPROJECT: (projectId: string | number, subProjectId: string | number) => string;
  CONVERTTO: (id: string | number) => string;
  EXPORT_EXCEL: string;
  EXPORTPROJECTOPENINGFORMPDF: (id: string | number) => string;
  UPLOAD_PROJECT_FROM_FILE: string;
  BILLING_SUMMARY: (id: string | number) => string;
  BILLING_SUMMARY_TEC_DETAILS: (id: string | number) => string;
  BILLING_SUMMARY_INVOICE_DETAILS: (id: string | number) => string;
  BILLING_SUMMARY_TIMESHEET_DETAILS: (id: string | number) => string;
  LAND_REGISTER_COVERAGE: (id: string | number) => string;
  SUBPROJECT_LAND_REGISTER_COVERAGE: (projectId: string | number, subProjectId: string | number) => string;
  ADOPT_GOOGLE_DUE_DATE: (id: string | number) => string;
  SUBPROJECT_ADOPT_GOOGLE_DUE_DATE: (projectId: string | number, subProjectId: string | number) => string;
};

const LogicalUnitEndpoints: LogicalUnitEndpointsType = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  SEARCH: `${LOGICALUNIT_URL}/search`,
  GET: (id: string | number) => `${LOGICALUNIT_URL}/${id}`,
  CREATE: `${LOGICALUNIT_URL}/create`,
  UPDATE: (id: string | number) => `${LOGICALUNIT_URL}/update/${id}`,
  DELETE: (id: string | number) => `${LOGICALUNIT_URL}/delete/${id}`,
  AUDITS: (id: string | number) => `${LOGICALUNIT_URL}/${id}/audits`,
  FINDEMPLOYEE: `${LOGICALUNIT_URL}/findEmployee`,
  FINDPROJECTMANAGER: `${LOGICALUNIT_URL}/findProjectManager`,
  FINDLANDSURVEYOR: `${LOGICALUNIT_URL}/findLandSurveyor`,
  FINDCUSTOMER: `${LOGICALUNIT_URL}/findCustomer`,
  FINDPROJECT: `${LOGICALUNIT_URL}/findProject`,
  FINDBILLINGPROFILES: `${LOGICALUNIT_URL}/findBillingProfiles`,
  FINDTASKASSIGNMENTS: `${LOGICALUNIT_URL}/findTaskAssignments`,
  FINDTASKS: `${LOGICALUNIT_URL}/findTasks`,
  GETSUBPROJECT: (projectId, subProjectId) => `${LOGICALUNIT_URL}/${projectId}/subproject/${subProjectId}`,
  GETSUBPROJECTAUDITS: (projectId, subProjectId) => `${LOGICALUNIT_URL}/${projectId}/subproject/${subProjectId}/audits`,
  CREATESUBPROJECT: (projectId) => `${LOGICALUNIT_URL}/${projectId}/subproject/create`,
  UPDATESUBPROJECT: (projectId, subProjectId) => `${LOGICALUNIT_URL}/${projectId}/subproject/${subProjectId}/update`,
  DELETESUBPROJECT: (projectId, subProjectId) => `${LOGICALUNIT_URL}/${projectId}/subproject/${subProjectId}/delete`,
  CHANGESTATUS: (id) => `${LOGICALUNIT_URL}/${id}/changeStatus`,
  CHANGESTATUSSUBPROJECT: (projectId, subProjectId) => `${LOGICALUNIT_URL}/${projectId}/subproject/${subProjectId}/changeStatus`,
  CONVERTTO: (id) => `${LOGICALUNIT_URL}/${id}/convertTo`,
  EXPORT_EXCEL: `${LOGICALUNIT_URL}/exportExcel`,
  EXPORTPROJECTOPENINGFORMPDF: (id) => `${LOGICALUNIT_URL}/${id}/exportProjectOpeningFormPdf`,
  UPLOAD_PROJECT_FROM_FILE: `${LOGICALUNIT_URL}/uploadProjectFromFile`,
  BILLING_SUMMARY: (id) => `${LOGICALUNIT_URL}/${id}/billingSummary`,
  BILLING_SUMMARY_TEC_DETAILS: (id) => `${LOGICALUNIT_URL}/${id}/billingSummary/details/tec`,
  BILLING_SUMMARY_INVOICE_DETAILS: (id) => `${LOGICALUNIT_URL}/${id}/billingSummary/details/invoices`,
  BILLING_SUMMARY_TIMESHEET_DETAILS: (id) => `${LOGICALUNIT_URL}/${id}/billingSummary/details/timesheets`,
  LAND_REGISTER_COVERAGE: (id) => `${LOGICALUNIT_URL}/${id}/projectToSurvey/landRegisterCoverage`,
  SUBPROJECT_LAND_REGISTER_COVERAGE: (projectId, subProjectId) =>
    `${LOGICALUNIT_URL}/${projectId}/subproject/${subProjectId}/projectToSurvey/landRegisterCoverage`,
  ADOPT_GOOGLE_DUE_DATE: (id) => `${LOGICALUNIT_URL}/${id}/projectToSurvey/adoptGoogleDueDate`,
  SUBPROJECT_ADOPT_GOOGLE_DUE_DATE: (projectId, subProjectId) =>
    `${LOGICALUNIT_URL}/${projectId}/subproject/${subProjectId}/projectToSurvey/adoptGoogleDueDate`,
};

/** Nœud personalization retourné par GET /U3121/initLU (personnalisations d'organisation, lecture seule). */
export interface U3121InitLUPersonalization {
  orgHourlyMarkup: string | null;
  admIncludeSubProject: string | null;
  /** Taux de vente horaire par défaut (chaîne numérique), pour préremplir le profil en mode Horaire. */
  defaultSellingRate?: string | null;
}

/** Normalise personalization initLU (camelCase / snake_case). */
export function normalizeU3121InitLUPersonalizationFromApi(p: U3121InitLUPersonalization | Record<string, unknown> | null | undefined): U3121InitLUPersonalization {
  const base: U3121InitLUPersonalization = {
    orgHourlyMarkup: null,
    admIncludeSubProject: null,
    defaultSellingRate: null,
  };
  if (!p || typeof p !== "object") return base;
  const r = p as Record<string, unknown>;
  return {
    orgHourlyMarkup: (r.orgHourlyMarkup ?? r.org_hourly_markup ?? null) as string | null,
    admIncludeSubProject: (r.admIncludeSubProject ?? r.adm_include_sub_project ?? null) as string | null,
    defaultSellingRate: (r.defaultSellingRate ?? r.defaultRate ?? r.default_rate ?? r.sellingRate ?? r.default_selling_rate ?? r.org_default_rate ?? null) as string | null,
  };
}

/** Parse la valeur personalization defaultSellingRate en nombre pour le formulaire. */
export function parseU3121DefaultSellingRateNumber(p: U3121InitLUPersonalization | null | undefined): number | undefined {
  const raw = p?.defaultSellingRate;
  if (raw == null || String(raw).trim() === "") return undefined;
  const normalized = String(raw).trim().replace(/\s+/g, "").replace(",", ".");
  const n = Number(normalized);
  return Number.isNaN(n) ? undefined : n;
}

/** Fréquence de facturation (aligné backend prBillingFrequency). */
export type U3121BillingFrequency = "EndProject" | "ByStep";

export interface U3121BillingProfile {
  id: string;
  billable?: boolean;
  fixedPrice?: boolean;
  fixedAmount?: number;
  hourly?: boolean;
  hourlyMarkup?: number;
  /** Taux de vente horaire (facturation horaire). Null si non applicable ou non renseigné. */
  sellingRate?: number | null;
  /** Date de début non facturable (yyyy-MM-dd). Lecture seule, fournie par l'API. */
  nonBillableStartDate?: string;
  /** Fréquence de facturation : EndProject (en fin de projet) ou ByStep (par étape). Requis. */
  billingFrequency?: U3121BillingFrequency;
  /** Note ou précision associée à la fréquence de facturation. Requis. */
  billingFrequencyNote?: string;
}

export interface U3121Employee {
  id: string;
  firstName?: string;
  lastName?: string;
  codeEmployee?: string;
}

/** Période de portée (employé + dates début/fin), retournée par les GET projet et sous-projet. Lecture seule. */
export interface U3121ScopePeriod {
  employee?: { id: string };
  /** Date de début (yyyy-MM-dd). */
  startDate?: string;
  /** Date de fin (yyyy-MM-dd). */
  endDate?: string;
}

/** Source d'une assignation employé : projet, sous-projet ou client (pour affichage colonne Source, comme Contacts). */
export type U3121EmployeeBillingAssignmentSource = "project" | "subproject" | "customer";

export interface U3121EmployeeBillingAssignment {
  id: string;
  /** Source de l'assignation (projet / sous-projet / client) ; fourni par l'API ou défini à l'ajout. */
  source?: U3121EmployeeBillingAssignmentSource;
  /** Libellés source fournis par le backend ; sinon le front traduit via i18n. */
  sourceLabelFr?: string;
  sourceLabelEn?: string;
  employee?: U3121Employee;
  task?: U3121Task;
  /** Taux horaire ; null si l'utilisateur n'a pas l'action VIEWEMPLOYEERATE (lecture seule API). */
  hourRate?: number | null;
  /** Date de début de validité (yyyy-MM-dd). */
  validFrom?: string;
  /** Date de fin de validité (yyyy-MM-dd). */
  validTo?: string;
}

export const emptyEmployeeBillingAssignment: U3121EmployeeBillingAssignment = {
  id: "",
  employee: undefined,
  task: undefined,
  hourRate: undefined,
  validFrom: undefined,
  validTo: undefined,
};

export interface U3121TaskAssignment {
  id: string;
}

export interface U3121Task {
  id: string;
  taskCode?: string;
  taskDescription?: string;
  taskDescriptionFr?: string;
  taskDescriptionEn?: string;
}

/** Assignation nature des travaux (id catalogue pr_work_nature). */
export interface U3121WorkNatureAssignment {
  id: string;
}

/** Nature des travaux (catalogue org, codes MyEvent CAD…OCTR). Distinct de pr_task / U3139. */
export interface U3121WorkNature {
  id: string;
  workNatureCode?: string;
  sortOrder?: number;
  workNatureDescription?: string;
  workNatureDescriptionFr?: string;
  workNatureDescriptionEn?: string;
  /** Punchables U3149 (GET /U3121/initLU — BL-0648). */
  punchableTaskIds?: string[];
}

/** Type d'échéance pour un projet à arpenter (aligné backend prDueDateType). */
export type U3121DueDateType = "Approximate" | "Concrete" | "RushPriorityFile";

/** Pays (objet minimal pour ProjectToSurvey, compatible affichage + envoi). */
export interface U3121ProjectToSurveyCountry {
  id: string;
  countryNameFr?: string;
  countryNameEn?: string;
  countryCode?: string;
  countryName?: string;
}

/** Province/état (objet minimal pour ProjectToSurvey). */
export interface U3121ProjectToSurveyState {
  id: string;
  stateNameFr?: string;
  stateNameEn?: string;
  stateName?: string;
}

/** Projet à arpenter (ProjectToSurvey), relation OneToOne optionnelle avec Project. */
/** Lot avec indicateur de présence au registre foncier (GET landRegisterCoverage, BL-0035). */
export interface U3121LandRegisterCoverageLot {
  lot: string;
  existsInRegister: boolean;
}

/** Couverture registre foncier pour le bloc projet à arpenter (BL-0035). */
export interface U3121LandRegisterCoverage {
  projectNumber: string;
  projectDescription: string;
  lots: U3121LandRegisterCoverageLot[];
}

export type U3121GoogleCalendarSyncStatus = "SYNCED" | "FAILED" | "PENDING" | "DELETED";

/** Statut sync Google Calendar (read-only, GET U3121). */
export interface U3121GoogleCalendarSync {
  status?: U3121GoogleCalendarSyncStatus | null;
  lastSyncAt?: string | null;
  lastError?: string | null;
  /** Alimenté par WebSocket uniquement (BL-0629). */
  dateDivergence?: U3121GoogleCalendarDateDivergence | null;
}

/** Réponse POST adoptGoogleDueDate (BL-0629). */
export interface U3121AdoptGoogleDueDateResult {
  dueDate: string;
  version: number;
}

export interface U3121ProjectToSurvey {
  id?: string;
  version?: number;
  /** Liste de lots (cadastre). API : string[] | null. Saisie par tags (U2000InputTag). */
  lot?: string[] | null;
  owner?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  country?: U3121ProjectToSurveyCountry | null;
  state?: U3121ProjectToSurveyState | null;
  dueDate?: string;
  dueDateType?: U3121DueDateType;
  dueDateNote?: string;
  deliveryTerms?: string;
  reference?: string;
  /** Ancien champ transient (tags) ; conservé pour compatibilité lecture API, formulaire utilise désormais reference (U2000InputText). */
  referenceTags?: string[];
  surveyor?: U3121Employee | null;
  note?: string | null;
  /** Sync Google Calendar (read-only, retourné par GET). */
  googleCalendarSync?: U3121GoogleCalendarSync | null;
}

export const emptyProjectToSurvey: U3121ProjectToSurvey = {
  lot: [],
  owner: "",
  address: "",
  city: "",
  postalCode: "",
  country: null,
  state: null,
  dueDate: "",
  dueDateType: "Approximate",
  dueDateNote: "",
  deliveryTerms: "",
  reference: "",
  surveyor: null,
  note: null,
};

/** Normalise projectToSurvey reçu de l'API pour le formulaire : lot en string[], referenceTags dérivé de reference. */
export function normalizeProjectToSurveyFromApi(pts: U3121ProjectToSurvey | null | undefined): void {
  if (pts == null || typeof pts !== "object") return;
  const lotVal = (pts as { lot?: unknown }).lot;
  if (typeof lotVal === "string") {
    const s = lotVal.trim() ? [lotVal.trim()] : [];
    (pts as U3121ProjectToSurvey).lot = s;
  }
  if (Array.isArray((pts as U3121ProjectToSurvey).lot) === false) {
    (pts as U3121ProjectToSurvey).lot = [];
  }
  if ((pts as U3121ProjectToSurvey).referenceTags === undefined && (pts as U3121ProjectToSurvey).reference !== undefined) {
    const ref = (pts as U3121ProjectToSurvey).reference;
    (pts as U3121ProjectToSurvey).referenceTags = typeof ref === "string" && ref.trim() ? [ref.trim()] : [];
  }
  const sync = (pts as U3121ProjectToSurvey).googleCalendarSync;
  if (sync && typeof sync === "object") {
    delete sync.dateDivergence;
  }
}

export interface U3121Project {
  id: string;
  projetNumber: string;
  projetName: string;
  projectStatus: boolean;
  /** Chef de projet (employé). */
  projectManager?: U3121Employee | null;
  /** Customer du projet. */
  customer?: U3121Customer | null;
  billingProfile: U3121BillingProfile;
  employeeBillingAssignment?: U3121EmployeeBillingAssignment | null;
  employeeBillingAssignments?: U3121EmployeeBillingAssignment[];
  /** Ids des tâches cochées (assignation). Synchronisé avec les checkboxes; envoyé au save. */
  taskAssignments?: U3121TaskAssignment[];
  /** Ids des natures des travaux cochées (onglet Nature des travaux). 0 autorisé. */
  workNatureAssignments?: U3121WorkNatureAssignment[];
  /** Fiche projet à arpenter (optionnel, maintenance embarquée). */
  projectToSurvey?: U3121ProjectToSurvey | null;
  /** Si true : afficher et envoyer le bloc Projet à arpenter ; si false : ne pas envoyer (projectToSurvey: null). */
  includeProjectToSurvey?: boolean;
  /** Liste fusionnée contacts client + projet (lecture seule), avec source pour l'affichage UI. */
  projectContacts?: U3121ProjectContactWithSource[];
  subprojects?: U3121SubProject[];
  /** Si false : ne pas afficher l'onglet Sous-projets dans le formulaire projet. */
  includeSubProjet?: boolean;
  /** Employés ayant accès à la feuille de temps (onglet Accès feuille de temps). */
  timesheetAccess?: U3121Employee[];
  /** Périodes de portée (dates début/fin par employé), lecture seule, retournées par le GET. */
  scopePeriods?: U3121ScopePeriod[];
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date | null;
}

/** Source d'un accès feuille de temps (affichage sous-projet : fusion projet + sous-projet). */
export type U3121TimesheetAccessSource = "project" | "subproject";

/** Employé avec source pour l'onglet Accès feuille de temps du sous-projet (liste fusionnée). */
export interface U3121TimesheetAccessWithSource extends U3121Employee {
  source?: U3121TimesheetAccessSource;
  sourceLabelFr?: string;
  sourceLabelEn?: string;
}

export interface U3121SubProject {
  id: string;
  projectId: string;
  project?: { id: string; projetNumber?: string; projetName?: string; timesheetAccess?: U3121Employee[] };
  subProjetNumber: string;
  subProjetName: string;
  subProjectStatus: boolean;
  billingProfile?: U3121BillingProfile | null;
  employeeBillingAssignment?: U3121EmployeeBillingAssignment | null;
  /** Liste des assignations employé (onglet assignation employé, même structure que projet). */
  employeeBillingAssignments?: U3121EmployeeBillingAssignment[];
  taskAssignment?: U3121TaskAssignment | null;
  /** Ids des tâches cochées (onglet assignation tâches, même structure que projet). */
  taskAssignments?: U3121TaskAssignment[];
  /** Nom du template à sauvegarder (transient, pour onglet tâches). */
  /** Ids des natures des travaux cochées (onglet Nature des travaux). */
  workNatureAssignments?: U3121WorkNatureAssignment[];
  /** Fiche projet à arpenter (onglet projet à arpenter, optionnel). */
  projectToSurvey?: U3121ProjectToSurvey | null;
  /** Si true : afficher et envoyer le bloc Profil de facturation ; si false : ne pas envoyer (billingProfile: null). Obligatoire create/update. */
  includeBillingProfile?: boolean;
  /** Si true : afficher et envoyer les assignations employés (facturation) ; si false : envoyer []. Obligatoire create/update. */
  includeEmployeeBillingAssignments?: boolean;
  /** Si true : afficher et envoyer les assignations tâches ; si false : envoyer []. Obligatoire create/update. */
  includeTaskAssignments?: boolean;
  /** Si true : afficher et envoyer les natures des travaux ; si false : envoyer []. Obligatoire create/update. */
  includeWorkNatureAssignments?: boolean;
  /** Si true : afficher et envoyer le bloc Projet à arpenter ; si false : ne pas envoyer (projectToSurvey: null). */
  includeProjectToSurvey?: boolean;
  /** Employés ayant accès à la feuille de temps (onglet Accès feuille de temps). */
  timesheetAccess?: U3121Employee[];
  /** Périodes de portée (dates début/fin par employé), lecture seule, retournées par le GET. */
  scopePeriods?: U3121ScopePeriod[];
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date | null;
}

export interface U3121FindEmployeeResult {
  id: string;
  codeEmployee?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

/** Customer (client) pour le projet. customerContacts optionnel pour affichage en lecture seule dans l'onglet Contact. */
export interface U3121Customer {
  id: string;
  customerCode?: string;
  customerName?: string;
  officePhone1?: string;
  officePhone2?: string;
  /** Contacts du client (affichage lecture seule dans l'onglet Contact projet). */
  customerContacts?: U3120CustomerContact[];
}

/** Contact projet : même structure que U3120CustomerContact (pr_contact scope project). */
export type U3121ProjectContact = U3120CustomerContact;

/** Contact avec source et libellés linguistiques (réponse GET projet : projectContacts). */
export interface U3121ProjectContactWithSource extends U3121ProjectContact {
  source: "customer" | "project";
  /** Libellés fournis par le backend ; sinon le front traduit source via i18n. */
  sourceLabelFr?: string;
  sourceLabelEn?: string;
}

export const emptyProjectContact: U3121ProjectContact = { ...emptyCustomerContact };

export const emptyBillingProfile: U3121BillingProfile = {
  id: "",
  billable: true,
  fixedPrice: false,
  fixedAmount: 0,
  hourly: false,
  sellingRate: null,
  hourlyMarkup: 0,
  billingFrequency: "EndProject",
  billingFrequencyNote: "",
};

/** Normalise le billingProfile reçu de l'API : forfaitaire->fixedPrice, montantForfaitaire->fixedAmount, horaire->hourly, majorationHoraire->hourlyMarkup. */
export function normalizeBillingProfileFromApi(bp: U3121BillingProfile | Record<string, unknown> | null | undefined): void {
  if (!bp || typeof bp !== "object") return;
  const b = bp as Record<string, unknown>;
  if (b.fixedPrice === undefined && b.forfaitaire !== undefined) b.fixedPrice = b.forfaitaire;
  if (b.fixedAmount === undefined && b.montantForfaitaire !== undefined) b.fixedAmount = b.montantForfaitaire;
  if (b.hourly === undefined && b.horaire !== undefined) b.hourly = b.horaire;
  if (b.hourlyMarkup === undefined && b.majorationHoraire !== undefined) b.hourlyMarkup = b.majorationHoraire;
  if (b.sellingRate === undefined && b.selling_rate !== undefined) {
    b.sellingRate = b.selling_rate as number | null;
  }
  if (b.nonBillableStartDate === undefined && (b as Record<string, unknown>).non_billable_start_date !== undefined) {
    b.nonBillableStartDate = (b as Record<string, unknown>).non_billable_start_date as string;
  }
  const profile = b as unknown as U3121BillingProfile;
  if (profile.billingFrequency === undefined) profile.billingFrequency = "EndProject";
  if (profile.billingFrequencyNote === undefined) profile.billingFrequencyNote = "";
}

export const emptyProject: U3121Project = {
  id: "",
  projetNumber: "",
  projetName: "",
  projectStatus: false,
  projectManager: null,
  customer: null,
  billingProfile: emptyBillingProfile,
  employeeBillingAssignment: null,
  employeeBillingAssignments: [],
  taskAssignments: [],
  workNatureAssignments: [],
  projectToSurvey: null,
  includeProjectToSurvey: true,
  projectContacts: [],
  subprojects: [],
  includeSubProjet: false,
  createdAt: new Date(),
  updatedAt: new Date(),
  deletedAt: null,
};

export const emptySubProject: U3121SubProject = {
  id: "",
  projectId: "",
  project: undefined,
  subProjetNumber: "",
  subProjetName: "",
  subProjectStatus: false,
  billingProfile: { ...emptyBillingProfile, billable: true },
  employeeBillingAssignment: null,
  employeeBillingAssignments: [],
  taskAssignment: null,
  taskAssignments: [],
  workNatureAssignments: [],
  projectToSurvey: { ...emptyProjectToSurvey },
  includeBillingProfile: true,
  includeEmployeeBillingAssignments: false,
  includeTaskAssignments: false,
  includeWorkNatureAssignments: false,
  includeProjectToSurvey: false,
  createdAt: new Date(),
  updatedAt: new Date(),
  deletedAt: null,
};

export const doInitLU = (axios: AxiosInstance) => axios.get(LogicalUnitEndpoints.INITLU);

export const doSearch = (axios: AxiosInstance, searchPayload: U2000SearchPayload) => axios.post(LogicalUnitEndpoints.SEARCH, searchPayload);

export const doExportExcel = (axios: AxiosInstance, filter: U2000SearchParameter, lang?: "fr" | "en") =>
  axios.post(LogicalUnitEndpoints.EXPORT_EXCEL, { filter, lang });

export const doGetData = (axios: AxiosInstance, id: string | number, forceRefresh?: boolean) => {
  const params = { ...(forceRefresh && { b: U2000GenerateUrlUUID() }) };
  return axios.get(LogicalUnitEndpoints.GET(id), { params });
};

/** Normalise projectToSurvey pour l'API : relations en { id }, lot en string[], reference (champ texte). */
function normalizeProjectToSurveyForSave(pts: U3121ProjectToSurvey | null | undefined): U3121ProjectToSurvey | null {
  if (pts == null || typeof pts !== "object") return null;
  const rawLot: unknown = pts.lot;
  const lotNorm = Array.isArray(rawLot) ? (rawLot as string[]) : (typeof rawLot === "string" && rawLot.trim() ? [rawLot.trim()] : []);
  const refStr = (pts.reference ?? "").trim();
  const merged: U3121ProjectToSurvey = {
    ...emptyProjectToSurvey,
    ...pts,
    lot: lotNorm,
    reference: refStr,
    country: pts.country?.id ? { id: pts.country.id } : null,
    state: pts.state?.id ? { id: pts.state.id } : null,
    surveyor:
      pts.surveyor &&
      typeof pts.surveyor === "object" &&
      pts.surveyor.id != null &&
      String(pts.surveyor.id).trim() !== ""
        ? { id: pts.surveyor.id }
        : null,
  };
  delete (merged as Record<string, unknown>)["referenceTags"];
  delete (merged as Record<string, unknown>)["googleCalendarSync"];
  return merged;
}

/** Retire taskAssignment du payload avant envoi (on n'envoie que taskAssignments). */
/** S'assure que billable est toujours true ou false, jamais null ou undefined. */
/** Inclut explicitement customer et projectManager pour create/update. */
/** Normalise projectToSurvey (relations en { id }). */
function projectPayloadForSave(data: U3121Project): U3121Project {
  const payload = JSON.parse(JSON.stringify(data)) as U3121Project;
  delete (payload as unknown as Record<string, unknown>)["taskAssignment"];
  delete (payload as unknown as Record<string, unknown>)["projetDescriptif"];
  delete (payload as unknown as Record<string, unknown>)["projectClassement"];
  // Création : le backend génère projetNumber ; ne pas envoyer de valeur utilisateur
  if (!data.id?.toString().trim()) payload.projetNumber = "";
  if (payload.billingProfile) {
    payload.billingProfile.billable = payload.billingProfile.billable ?? false;
    delete (payload.billingProfile as unknown as Record<string, unknown>).nonBillableStartDate;
    if (!payload.billingProfile.hourly) {
      delete (payload.billingProfile as unknown as Record<string, unknown>).sellingRate;
    }
  }
  payload.customer = data.customer?.id
    ? {
        id: data.customer.id,
        customerCode: data.customer.customerCode,
        customerName: data.customer.customerName,
        officePhone1: data.customer.officePhone1,
        officePhone2: data.customer.officePhone2,
      }
    : null;
  const pm = data.projectManager;
  const pmHasId = pm && typeof pm === "object" && pm.id != null && String(pm.id).trim() !== "";
  payload.projectManager = pmHasId
    ? {
        id: pm.id,
        firstName: pm.firstName,
        lastName: pm.lastName,
        codeEmployee: pm.codeEmployee,
      }
    : null;
  if (payload.includeProjectToSurvey === true) {
    payload.projectToSurvey = normalizeProjectToSurveyForSave(data.projectToSurvey) ?? { ...emptyProjectToSurvey };
  } else {
    payload.projectToSurvey = null;
  }
  payload.projectContacts = Array.isArray(data.projectContacts) ? data.projectContacts : [];
  // Ne pas envoyer les assignations employés dont la source est customer (elles appartiennent au client).
  const rawAssignments = Array.isArray(data.employeeBillingAssignments) ? data.employeeBillingAssignments : [];
  payload.employeeBillingAssignments = rawAssignments.filter((a) => a.source !== "customer");
  delete (payload as unknown as Record<string, unknown>)["workNatureTemplateNameToSave"];
  delete (payload as unknown as Record<string, unknown>)["taskTemplateNameToSave"];
  payload.workNatureAssignments = Array.isArray(data.workNatureAssignments) ? data.workNatureAssignments : [];
  payload.timesheetAccess = Array.isArray(data.timesheetAccess) ? data.timesheetAccess : [];
  return payload;
}

export const doCreateData = (axios: AxiosInstance, data: U3121Project) => axios.post(LogicalUnitEndpoints.CREATE, projectPayloadForSave(data));

export const doUpdateData = (axios: AxiosInstance, id: string | number, data: U3121Project) => axios.post(LogicalUnitEndpoints.UPDATE(id), projectPayloadForSave(data));

export const doDeleteData = (axios: AxiosInstance, id: string | number, data?: U3121Project) => axios.post(LogicalUnitEndpoints.DELETE(id), data);

export const doGetAuditsData = (axios: AxiosInstance, id: string | number) => axios.get(LogicalUnitEndpoints.AUDITS(id), { params: { b: U2000GenerateUrlUUID() } });

export const doFindEmployee = (axios: AxiosInstance, criterias: { fieldName: string; value: unknown }[], pageSize?: number) =>
  axios.post(LogicalUnitEndpoints.FINDEMPLOYEE, criterias, { params: pageSize != null ? { pageSize } : {} });

export const doFindProjectManager = (axios: AxiosInstance, criterias: { fieldName: string; value: unknown }[], pageSize?: number) =>
  axios.post(LogicalUnitEndpoints.FINDPROJECTMANAGER, criterias, { params: pageSize != null ? { pageSize } : {} });

/** Recherche d'employés avec le profil Arpenteur-géomètre (operationType landSurveyor). */
export const doFindLandSurveyor = (axios: AxiosInstance, criterias: { fieldName: string; value: unknown }[], pageSize?: number) =>
  axios.post(LogicalUnitEndpoints.FINDLANDSURVEYOR, criterias, { params: pageSize != null ? { pageSize } : {} });

export const doFindCustomer = (axios: AxiosInstance, criterias: { fieldName: string; value: unknown }[], pageSize?: number) =>
  axios.post(LogicalUnitEndpoints.FINDCUSTOMER, criterias, { params: pageSize != null ? { pageSize } : {} });

export type U3121FindProjectItem = {
  id: string;
  projetNumber?: string;
  projetName?: string;
  customerCode?: string;
  customerName?: string;
  /** true = actif ; false = inactif (retourné par findProject avec includeInactive). */
  projectStatus?: boolean;
  projectDisplay?: string;
  projetDisplay?: string;
};

/** Meta critère recherche liste — inclut projets inactifs (BL-0346). À injecter uniquement depuis criteriaLookup. */
export const U3121_FIND_PROJECT_INCLUDE_INACTIVE_CRITERION = {
  fieldName: 'includeInactive',
  value: true,
} as const;

/** Libellé autocomplete / chip projet avec suffixe inactif optionnel (BL-0346). */
export function u3121FormatProjectLookupLabel(
  item: U3121FindProjectItem | Record<string, unknown> | null | undefined,
  inactiveSuffix: string,
): string {
  if (!item || typeof item !== 'object') return '';
  const rec = item as Record<string, unknown>;
  const display = String(rec.projectDisplay ?? rec.projetDisplay ?? '').trim();
  const num = String(rec.projetNumber ?? '').trim();
  const name = String(rec.projetName ?? '').trim();
  let label = display;
  if (!label) {
    if (num && name) label = `${num} — ${name}`;
    else label = num || name || '';
  }
  if (!label) return '';
  const projectStatus = rec.projectStatus;
  if (projectStatus === false && inactiveSuffix.trim()) {
    return `${label} (${inactiveSuffix.trim()})`;
  }
  return label;
}

export const doFindProject = (axios: AxiosInstance, criterias: { fieldName: string; value: unknown }[], pageSize?: number) =>
  axios.post(LogicalUnitEndpoints.FINDPROJECT, criterias, { params: pageSize != null ? { pageSize } : {} });

export const doFindBillingProfiles = (axios: AxiosInstance, criterias: { fieldName: string; value: unknown }[]) => axios.post(LogicalUnitEndpoints.FINDBILLINGPROFILES, criterias);

export const doFindTaskAssignments = (axios: AxiosInstance, criterias: { fieldName: string; value: unknown }[]) => axios.post(LogicalUnitEndpoints.FINDTASKASSIGNMENTS, criterias);

export const doFindTasks = (axios: AxiosInstance, criterias: { fieldName: string; value: unknown }[]) => axios.post(LogicalUnitEndpoints.FINDTASKS, criterias);

/**
 * Prépare le payload sous-projet pour create/update selon les 3 booléens obligatoires.
 * - includeBillingProfile: false → billingProfile: null ; true → billingProfile avec les champs requis.
 * - includeEmployeeBillingAssignments: false → employeeBillingAssignments: [] ; true → tableau (éventuellement vide).
 * - includeTaskAssignments: false → taskAssignments: [] ; true → tableau (éventuellement vide).
 * - includeWorkNatureAssignments: false → workNatureAssignments: [] ; true → tableau (éventuellement vide).
 */
export function subProjectPayloadForSave(sub: U3121SubProject, projectId: string): U3121SubProject {
  const payload = JSON.parse(JSON.stringify(sub)) as U3121SubProject;
  payload.includeBillingProfile = Boolean(sub.includeBillingProfile);
  payload.includeEmployeeBillingAssignments = Boolean(sub.includeEmployeeBillingAssignments);
  payload.includeTaskAssignments = Boolean(sub.includeTaskAssignments);
  payload.includeWorkNatureAssignments = Boolean(sub.includeWorkNatureAssignments);
  payload.includeProjectToSurvey = Boolean(sub.includeProjectToSurvey);
  payload.project = payload.project ?? { id: sub.projectId || projectId };

  if (!payload.includeBillingProfile) {
    payload.billingProfile = null;
  } else if (payload.billingProfile) {
    (payload.billingProfile as U3121BillingProfile).billable = payload.billingProfile.billable ?? false;
    delete (payload.billingProfile as unknown as Record<string, unknown>).nonBillableStartDate;
    if (!payload.billingProfile.hourly) {
      delete (payload.billingProfile as unknown as Record<string, unknown>).sellingRate;
    }
  }

  if (!payload.includeEmployeeBillingAssignments) {
    payload.employeeBillingAssignments = [];
  } else {
    const raw = Array.isArray(sub.employeeBillingAssignments) ? sub.employeeBillingAssignments : [];
    // Ne pas envoyer les assignations dont la source est customer ou project (elles appartiennent au projet ou au client).
    payload.employeeBillingAssignments = raw.filter((a) => a.source !== "customer" && a.source !== "project");
  }

  if (!payload.includeTaskAssignments) {
    payload.taskAssignments = [];
  } else {
    payload.taskAssignments = Array.isArray(sub.taskAssignments) ? sub.taskAssignments : [];
  }

  if (!payload.includeWorkNatureAssignments) {
    payload.workNatureAssignments = [];
  } else {
    payload.workNatureAssignments = Array.isArray(sub.workNatureAssignments) ? sub.workNatureAssignments : [];
  }

  if (!payload.includeProjectToSurvey) {
    payload.projectToSurvey = null;
  } else {
    payload.projectToSurvey = normalizeProjectToSurveyForSave(sub.projectToSurvey);
  }

  delete (payload as unknown as Record<string, unknown>)["taskAssignment"];
  delete (payload as unknown as Record<string, unknown>)["employeeBillingAssignment"];
  delete (payload as unknown as Record<string, unknown>)["workNatureTemplateNameToSave"];
  // N'envoyer que les accès feuille de temps du sous-projet (source === "subproject"), sans le préfixe d'id.
  const rawTimesheet = Array.isArray(sub.timesheetAccess) ? sub.timesheetAccess : [];
  const subprojectPrefix = "subproject_";
  payload.timesheetAccess = rawTimesheet
    .filter((r) => (r as { source?: string }).source === "subproject")
    .map((r) => ({
      id: (r.id as string).startsWith(subprojectPrefix) ? (r.id as string).slice(subprojectPrefix.length) : r.id,
      firstName: r.firstName,
      lastName: r.lastName,
      codeEmployee: r.codeEmployee,
    }));
  return payload;
}

export const doGetSubProjectData = (axios: AxiosInstance, projectId: string | number, subProjectId: string | number, forceRefresh?: boolean) => {
  const params = { ...(forceRefresh && { b: U2000GenerateUrlUUID() }) };
  return axios.get(LogicalUnitEndpoints.GETSUBPROJECT(projectId, subProjectId), { params });
};

export const doGetSubProjectAuditsData = (axios: AxiosInstance, projectId: string | number, subProjectId: string | number) =>
  axios.get(LogicalUnitEndpoints.GETSUBPROJECTAUDITS(projectId, subProjectId), { params: { b: U2000GenerateUrlUUID() } });

export const doCreateSubProjectData = (axios: AxiosInstance, projectId: string | number, data: U3121SubProject) => axios.post(LogicalUnitEndpoints.CREATESUBPROJECT(projectId), data);

export const doUpdateSubProjectData = (axios: AxiosInstance, projectId: string | number, subProjectId: string | number, data: U3121SubProject) =>
  axios.post(LogicalUnitEndpoints.UPDATESUBPROJECT(projectId, subProjectId), data);

export const doDeleteSubProjectData = (axios: AxiosInstance, projectId: string | number, subProjectId: string | number, data: U3121SubProject) =>
  axios.post(LogicalUnitEndpoints.DELETESUBPROJECT(projectId, subProjectId), data);

/** Statut envoyé à l'endpoint ChangeStatus : Close (projet actif → fermer) ou Reopen (projet inactif → rouvrir). */
export type U3121ChangeStatusAction = "Close" | "Reopen";

export const doChangeStatusProject = (axios: AxiosInstance, id: string | number, status: U3121ChangeStatusAction) => axios.post(LogicalUnitEndpoints.CHANGESTATUS(id), { status });

export const doChangeStatusSubProject = (axios: AxiosInstance, projectId: string | number, subProjectId: string | number, status: U3121ChangeStatusAction) =>
  axios.post(LogicalUnitEndpoints.CHANGESTATUSSUBPROJECT(projectId, subProjectId), { status });

/** Valeur du payload pour convertTo : "Project" (inclut sous-projets → convertir en projet simple) ou "SubProject" (projet simple → convertir avec sous-projets). */
export type U3121ConvertToAction = "Project" | "SubProject";

export const doConvertToProject = (axios: AxiosInstance, id: string | number, to: U3121ConvertToAction) => axios.post(LogicalUnitEndpoints.CONVERTTO(id), { to });

/** Lance la génération asynchrone du PDF (201 + job). Le PDF est livré via la notification (centre de notification). */
export const doExportProjectOpeningFormPdf = (
  axios: AxiosInstance,
  id: string | number,
  payload?: { lang?: "fr" | "en" }
) => axios.post(LogicalUnitEndpoints.EXPORTPROJECTOPENINGFORMPDF(id), payload ?? {});

/** POST création de projet à partir d'un fichier texte (multipart, champ "file", max 1 Mo). Réponse 201 avec { result: U3121Project }. */
export const doUploadProjectFromFile = (axios: AxiosInstance, formData: FormData) =>
  axios.post(LogicalUnitEndpoints.UPLOAD_PROJECT_FROM_FILE, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

/** Codes d'erreur API U3121 (BU3121XX) et B0008. */
export const U3121_ERROR_PROJECT_NUMBER_DUPLICATE = "BU312101";
export const U3121_ERROR_PROJECT_ID_MISMATCH = "BU312102";
export const U3121_ERROR_SUBPROJECT_NUMBER_DUPLICATE = "BU312103";
export const U3121_ERROR_EBA_VALIDFROM_REQUIRED = "BU312104";
export const U3121_ERROR_UPLOAD_INVALID_FILE_TYPE = "BU312105";
export const U3121_ERROR_UPLOAD_DEFAULT_COUNTRY_STATE = "BU312106";
export const U3121_ERROR_UPLOAD_INVALID_BUDGET = "BU312107";
export const COMMON_ERROR_ENTITY_ID_MISMATCH = "B0008";

/** errorType pour distinguer les usages de BU312103. */
export const U3121_ERROR_TYPE_SUBPROJECT_NUMBER_DUPLICATE = "SubProjectNumberDuplicateError";
export const U3121_ERROR_TYPE_EBA_VALIDATION = "EmployeeBillingAssignmentValidationError";

/** Indice de préparation à la facturation (GET billingSummary). */
export type U3121BillingReadinessHint = "ready" | "blocked_by_timesheets" | "has_uninvoiced_wip" | "not_billable";

export type U3121BillingTecPool = "uninvoiced" | "invoiced" | "writeOff";

export type U3121BillingDocumentNature = "STANDARD" | "CREDIT" | "ADVANCE" | "VOID";

/** Nature métier d'une ligne détail facture (hors filtre query `VOID`). */
export type U3121BillingInvoiceRowDocumentNature = "STANDARD" | "CREDIT" | "ADVANCE" | "WIP_WRITE_OFF";

export interface U3121BillingKindAggregate {
  kind?: string;
  lineCount?: number;
  totalAmount?: number;
  totalCostAmount?: number;
}

export interface U3121BillingSubProjectAggregate {
  subProjectId?: string;
  subProjetNumber?: string;
  subProjetName?: string;
  lineCount?: number;
  totalAmount?: number;
  totalCostAmount?: number;
}

export interface U3121BillingTecPoolSummary {
  lineCount: number;
  totalHours: number;
  totalAmount: number;
  totalCostAmount: number;
  byKind: U3121BillingKindAggregate[];
  bySubProject?: U3121BillingSubProjectAggregate[];
}

export interface U3121BillingTecSummary {
  uninvoiced: U3121BillingTecPoolSummary;
  invoiced: U3121BillingTecPoolSummary;
  writeOff: U3121BillingTecPoolSummary;
}

export interface U3121BillingStandardDocumentsSummary {
  documentCount: number;
  totalGrossAmountInvoiceTaxable: number;
  totalGrossAmountInvoiceNonTaxable: number;
  totalInvoicedAmount: number;
  totalPaymentsReceived: number;
  totalBalanceDue: number;
}

/**
 * Agrégats notes de crédit ou acomptes (billingSummary).
 * Carte « Notes de crédit » : totaux en **avant taxes** (alignés `sourceUsageSummary` U3125).
 * Champs ventilés (BL-0042) : HT ; appliqué/restant ventilés = applications **explicites** uniquement.
 * Carte « Acomptes » : totaux en **TTC** (champs ventilés parsés à 0 si absents de l’API).
 */
export interface U3121BillingCreditAdvanceDocumentsSummary {
  documentCount: number;
  totalOriginalAmount: number;
  totalOriginalAmountTaxable: number;
  totalOriginalAmountNonTaxable: number;
  totalAppliedAmount: number;
  totalAppliedAmountTaxable: number;
  totalAppliedAmountNonTaxable: number;
  totalRemainingAmount: number;
  totalRemainingAmountTaxable: number;
  totalRemainingAmountNonTaxable: number;
  totalPaymentsReceived?: number;
}

export interface U3121BillingVoidNatureBucket {
  count: number;
  amount: number;
}

export interface U3121BillingVoidDocumentsSummary {
  documentCount: number;
  byNature: {
    standard: U3121BillingVoidNatureBucket;
    credit: U3121BillingVoidNatureBucket;
    advance: U3121BillingVoidNatureBucket;
    wipWriteOff: U3121BillingVoidNatureBucket;
  };
  totalInvoicedAmount: number;
}

export interface U3121BillingDocumentsSummary {
  standard: U3121BillingStandardDocumentsSummary;
  creditNotes: U3121BillingCreditAdvanceDocumentsSummary;
  advances: U3121BillingCreditAdvanceDocumentsSummary;
  void: U3121BillingVoidDocumentsSummary;
}

export interface U3121BillingUnapprovedTimesheetsSummary {
  count: number;
  totalReportedHours: number;
  totalExpenseAmount: number;
  totalEquipmentAmount: number;
}

export type U3121BillingProjectProfitabilityMode = "FIXED_FEE" | "TIME_AND_MATERIAL" | "NOT_APPLICABLE";

export type U3121BillingProjectProfitabilityStatus =
  | "profitable"
  | "at_risk"
  | "unprofitable"
  | "not_applicable"
  | "insufficient_data";

export interface U3121BillingProjectProfitabilityByKind {
  transactionKind: string;
  lineCount: number;
  totalCostAmount: number;
  totalSellingAmount: number;
}

export interface U3121BillingProjectProfitability {
  mode: U3121BillingProjectProfitabilityMode;
  status: U3121BillingProjectProfitabilityStatus;
  referenceRevenue: number | null;
  actualCost: number;
  actualRevenue: number | null;
  profitOrMarginAmount: number | null;
  profitOrMarginPercent: number | null;
  multiplier: number | null;
  costConsumedPercent: number | null;
  byKind: U3121BillingProjectProfitabilityByKind[];
}

export interface U3121BillingSummary {
  projectId: string;
  billable: boolean;
  includeSubProjet: boolean;
  canViewWip: boolean;
  canViewInvoices: boolean;
  canViewTimesheets: boolean;
  tec: U3121BillingTecSummary | null;
  billingDocuments: U3121BillingDocumentsSummary | null;
  unapprovedTimesheets: U3121BillingUnapprovedTimesheetsSummary | null;
  readinessHint: U3121BillingReadinessHint;
  projectProfitability: U3121BillingProjectProfitability | null;
}

export interface U3121BillingInvoiceConsumption {
  targetInvoiceId?: string;
  targetInvoiceNumber?: string | number | null;
  targetInvoiceDate?: string | null;
  targetInvoiceStatus?: string | null;
  amount?: number;
}

export interface U3121BillingInvoiceDetailLine {
  id: string;
  documentNature?: U3121BillingInvoiceRowDocumentNature;
  invoiceNumber?: string | number | null;
  invoiceDate?: string | null;
  status?: string | null;
  /** Alias pour colonnes grille (même valeur que `status`). */
  invoiceStatus?: string | null;
  invoiceTypeCode?: string;
  invoiceTypeLabelFr?: string;
  invoiceTypeLabelEn?: string;
  amountTec?: number;
  /** BL-0020: montants HT bruts avant crédit (STANDARD). */
  grossAmountInvoiceTaxable?: number;
  grossAmountInvoiceNonTaxable?: number;
  /** Alias historique (compatibilité mapping backend/front). */
  amountInvoiceTaxable?: number;
  amountInvoiceNonTaxable?: number;
  taxAmount1?: number | null;
  taxAmount2?: number | null;
  invoiceTotal?: number;
  paymentsReceived?: number;
  balanceDue?: number;
  originalAmount?: number;
  /** BL-0042 : \|HT taxable\| en-tête (CREDIT). */
  originalAmountTaxable?: number;
  originalAmountNonTaxable?: number;
  appliedAmount?: number;
  appliedAmountTaxable?: number;
  appliedAmountNonTaxable?: number;
  remainingAmount?: number;
  remainingAmountTaxable?: number;
  remainingAmountNonTaxable?: number;
  fullyConsumed?: boolean;
  consumptions?: U3121BillingInvoiceConsumption[];
}

export interface U3121BillingTimesheetDetailLine {
  id: string;
  periodDate?: string | null;
  status?: string | null;
  employee?: {
    id?: string;
    codeEmployee?: string | null;
    firstName?: string | null;
    lastName?: string | null;
  } | null;
  totalReportedHours: number;
  projectHonorariumHours?: number;
  projectExpenseAmount: number;
  projectEquipmentAmount: number;
  /** Champs plats pour la grille (dérivés au parse). */
  employeeCode?: string | null;
  employeeFirstName?: string | null;
  employeeLastName?: string | null;
  periodStart?: string | null;
  reportedHours?: number;
  expenseAmount?: number;
  equipmentAmount?: number;
}

/** Statuts FT non approuvés dans le détail sommaire : saisie / soumission → U3129. */
const U3121_BILLING_TIMESHEET_NON_APPROVED_STATUSES = new Set(["Open", "WaitingForApproval"]);

/** Route applicative (path relatif) pour ouvrir une FT du détail sommaire en nouvel onglet. */
export function u3121BillingSummaryTimesheetOpenPath(line: Pick<U3121BillingTimesheetDetailLine, "id" | "status">): string | null {
  const id = line.id?.trim();
  if (!id) return null;
  const status = (line.status ?? "").trim();
  const unit = U3121_BILLING_TIMESHEET_NON_APPROVED_STATUSES.has(status) ? "U3129" : "U3130";
  return `/${unit}/${id}`;
}

/** Ligne détail TEC (GET billingSummary/details/tec) — alignée U3134 / affichage WIP. */
export interface U3121BillingTecDetailLine {
  id: string;
  transactionKind?: U3134TransactionKind | string;
  honorariumEntryKind?: string | null;
  transactionDate?: string | null;
  quantity?: number;
  amount?: number | null;
  costAmount?: number | null;
  sellingRate?: number | null;
  costRate?: number | null;
  sellingMarkupFactor?: number | null;
  costMarkupFactor?: number | null;
  billable?: boolean;
  hourly?: boolean | null;
  remark?: string | null;
  employee?: {
    id?: string;
    codeEmployee?: string | null;
    firstName?: string | null;
    lastName?: string | null;
  } | null;
  task?: { id?: string; taskCode?: string | null } | null;
  subProject?: { id?: string; subProjetNumber?: string; subProjetName?: string } | null;
  invoice?: {
    id?: string;
    invoiceNumber?: string | null;
    documentNature?: string;
    status?: string;
  } | null;
  /** Si `transactionKind = expense` (aligné U3134). */
  expenseType?: U3121BillingExpenseTypeRef | null;
  /** Si `transactionKind = equipment` (aligné U3134). */
  equipmentType?: U3121BillingEquipmentTypeRef | null;
}

/** Type de dépense sur ligne détail TEC (`GET …/details/tec`). */
export interface U3121BillingExpenseTypeRef {
  id?: string;
  code?: string | null;
  description?: string | null;
}

/** Type d’équipement sur ligne détail TEC (`GET …/details/tec`). */
export interface U3121BillingEquipmentTypeRef {
  id?: string;
  code?: string | null;
  description?: string | null;
  billingUnit?: string | null;
}

function parseU3121BillingExpenseTypeRef(raw: unknown): U3121BillingExpenseTypeRef | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  if (o.id == null && o.code == null && o.description == null) return null;
  return {
    id: o.id != null ? String(o.id) : undefined,
    code: (o.code as string | null | undefined) ?? null,
    description: (o.description as string | null | undefined) ?? null,
  };
}

function parseU3121BillingEquipmentTypeRef(raw: unknown): U3121BillingEquipmentTypeRef | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  if (o.id == null && o.code == null && o.description == null && o.billingUnit == null) return null;
  return {
    id: o.id != null ? String(o.id) : undefined,
    code: (o.code as string | null | undefined) ?? null,
    description: (o.description as string | null | undefined) ?? null,
    billingUnit: (o.billingUnit ?? o.billing_unit) as string | null | undefined,
  };
}

function extractBillingSummaryResultsList(data: unknown): Record<string, unknown>[] {
  if (Array.isArray(data)) {
    return data.map((item) => (item ?? {}) as Record<string, unknown>);
  }
  const root = data as { results?: unknown[]; result?: unknown };
  const list = Array.isArray(root?.results) ? root.results : Array.isArray(root?.result) ? root.result : [];
  return list.map((item) => (item ?? {}) as Record<string, unknown>);
}

/** Prépare la ligne API TEC pour `normalizeU3125WipTransactionLine` (unité de facturation équipement). */
function prepareBillingTecDetailRawForWipNormalize(raw: Record<string, unknown>): Record<string, unknown> {
  const eq = raw.equipmentType ?? raw.equipment_type;
  if (eq && typeof eq === "object" && !Array.isArray(eq)) {
    const o = eq as Record<string, unknown>;
    const desc = String(o.description ?? "").trim();
    const unit = String(o.billingUnit ?? o.billing_unit ?? "").trim();
    if (desc && unit) {
      return { ...raw, equipmentType: { ...o, description: `${desc} (${unit})` } };
    }
  }
  return raw;
}

function numOrZero(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function parseBillingKindAggregate(raw: unknown): U3121BillingKindAggregate {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    kind: (r.kind ?? r.transactionKind) as string | undefined,
    lineCount: numOrZero(r.lineCount),
    totalAmount: numOrZero(r.totalAmount),
    totalCostAmount: numOrZero(r.totalCostAmount),
  };
}

function parseBillingSubProjectAggregate(raw: unknown): U3121BillingSubProjectAggregate {
  const r = (raw ?? {}) as Record<string, unknown>;
  const sp = (r.subProject ?? r.subproject) as Record<string, unknown> | undefined;
  return {
    subProjectId: String(r.subProjectId ?? sp?.id ?? ""),
    subProjetNumber: String(r.subProjetNumber ?? sp?.subProjetNumber ?? sp?.sub_projet_number ?? ""),
    subProjetName: String(r.subProjetName ?? sp?.subProjetName ?? sp?.sub_projet_name ?? ""),
    lineCount: numOrZero(r.lineCount),
    totalAmount: numOrZero(r.totalAmount),
    totalCostAmount: numOrZero(r.totalCostAmount),
  };
}

function parseBillingTecPoolSummary(raw: unknown): U3121BillingTecPoolSummary {
  const r = (raw ?? {}) as Record<string, unknown>;
  const byKindRaw = Array.isArray(r.byKind) ? r.byKind : [];
  const bySubRaw = Array.isArray(r.bySubProject) ? r.bySubProject : [];
  return {
    lineCount: numOrZero(r.lineCount),
    totalHours: numOrZero(r.totalHours),
    totalAmount: numOrZero(r.totalAmount),
    totalCostAmount: numOrZero(r.totalCostAmount),
    byKind: byKindRaw.map(parseBillingKindAggregate),
    bySubProject: bySubRaw.length > 0 ? bySubRaw.map(parseBillingSubProjectAggregate) : undefined,
  };
}

function parseBillingTecSummary(raw: unknown): U3121BillingTecSummary {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    uninvoiced: parseBillingTecPoolSummary(r.uninvoiced),
    invoiced: parseBillingTecPoolSummary(r.invoiced),
    writeOff: parseBillingTecPoolSummary(r.writeOff),
  };
}

function parseBillingStandardDocuments(raw: unknown): U3121BillingStandardDocumentsSummary {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    documentCount: numOrZero(r.documentCount),
    totalGrossAmountInvoiceTaxable: numOrZero(r.totalGrossAmountInvoiceTaxable ?? r.totalAmountInvoiceTaxable),
    totalGrossAmountInvoiceNonTaxable: numOrZero(r.totalGrossAmountInvoiceNonTaxable ?? r.totalAmountInvoiceNonTaxable),
    totalInvoicedAmount: numOrZero(r.totalInvoicedAmount),
    totalPaymentsReceived: numOrZero(r.totalPaymentsReceived),
    totalBalanceDue: numOrZero(r.totalBalanceDue),
  };
}

function parseBillingCreditAdvanceDocuments(raw: unknown): U3121BillingCreditAdvanceDocumentsSummary {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    documentCount: numOrZero(r.documentCount),
    totalOriginalAmount: numOrZero(r.totalOriginalAmount),
    totalOriginalAmountTaxable: numOrZero(r.totalOriginalAmountTaxable ?? r.total_original_amount_taxable),
    totalOriginalAmountNonTaxable: numOrZero(r.totalOriginalAmountNonTaxable ?? r.total_original_amount_non_taxable),
    totalAppliedAmount: numOrZero(r.totalAppliedAmount),
    totalAppliedAmountTaxable: numOrZero(r.totalAppliedAmountTaxable ?? r.total_applied_amount_taxable),
    totalAppliedAmountNonTaxable: numOrZero(r.totalAppliedAmountNonTaxable ?? r.total_applied_amount_non_taxable),
    totalRemainingAmount: numOrZero(r.totalRemainingAmount),
    totalRemainingAmountTaxable: numOrZero(r.totalRemainingAmountTaxable ?? r.total_remaining_amount_taxable),
    totalRemainingAmountNonTaxable: numOrZero(r.totalRemainingAmountNonTaxable ?? r.total_remaining_amount_non_taxable),
    totalPaymentsReceived: r.totalPaymentsReceived != null ? numOrZero(r.totalPaymentsReceived) : undefined,
  };
}

function parseBillingVoidNatureBucket(raw: unknown): U3121BillingVoidNatureBucket {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    count: numOrZero(r.count),
    amount: numOrZero(r.amount),
  };
}

function emptyBillingVoidNatureBucket(): U3121BillingVoidNatureBucket {
  return { count: 0, amount: 0 };
}

function parseBillingVoidDocuments(raw: unknown): U3121BillingVoidDocumentsSummary {
  const r = (raw ?? {}) as Record<string, unknown>;
  const byNatureRaw = (r.byNature ?? r.by_nature ?? {}) as Record<string, unknown>;
  return {
    documentCount: numOrZero(r.documentCount),
    byNature: {
      standard: parseBillingVoidNatureBucket(byNatureRaw.standard ?? emptyBillingVoidNatureBucket()),
      credit: parseBillingVoidNatureBucket(byNatureRaw.credit ?? emptyBillingVoidNatureBucket()),
      advance: parseBillingVoidNatureBucket(byNatureRaw.advance ?? emptyBillingVoidNatureBucket()),
      wipWriteOff: parseBillingVoidNatureBucket(
        byNatureRaw.wipWriteOff ?? byNatureRaw.wip_write_off ?? emptyBillingVoidNatureBucket(),
      ),
    },
    totalInvoicedAmount: numOrZero(r.totalInvoicedAmount),
  };
}

function parseBillingDocumentsSummary(raw: unknown): U3121BillingDocumentsSummary {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    standard: parseBillingStandardDocuments(r.standard),
    creditNotes: parseBillingCreditAdvanceDocuments(r.creditNotes),
    advances: parseBillingCreditAdvanceDocuments(r.advances),
    void: parseBillingVoidDocuments(r.void),
  };
}

function parseBillingUnapprovedTimesheets(raw: unknown): U3121BillingUnapprovedTimesheetsSummary {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    count: numOrZero(r.count),
    totalReportedHours: numOrZero(r.totalReportedHours),
    totalExpenseAmount: numOrZero(r.totalExpenseAmount),
    totalEquipmentAmount: numOrZero(r.totalEquipmentAmount),
  };
}

const BILLING_READINESS_HINTS: U3121BillingReadinessHint[] = ["ready", "blocked_by_timesheets", "has_uninvoiced_wip", "not_billable"];

const BILLING_PROFITABILITY_MODES: U3121BillingProjectProfitabilityMode[] = ["FIXED_FEE", "TIME_AND_MATERIAL", "NOT_APPLICABLE"];

const BILLING_PROFITABILITY_STATUSES: U3121BillingProjectProfitabilityStatus[] = [
  "profitable",
  "at_risk",
  "unprofitable",
  "not_applicable",
  "insufficient_data",
];

function numOrNull(v: unknown): number | null {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function parseBillingReadinessHint(raw: unknown): U3121BillingReadinessHint {
  const s = String(raw ?? "ready");
  return BILLING_READINESS_HINTS.includes(s as U3121BillingReadinessHint) ? (s as U3121BillingReadinessHint) : "ready";
}

function parseBillingProjectProfitabilityMode(raw: unknown): U3121BillingProjectProfitabilityMode {
  const s = String(raw ?? "NOT_APPLICABLE");
  return BILLING_PROFITABILITY_MODES.includes(s as U3121BillingProjectProfitabilityMode)
    ? (s as U3121BillingProjectProfitabilityMode)
    : "NOT_APPLICABLE";
}

function parseBillingProjectProfitabilityStatus(raw: unknown): U3121BillingProjectProfitabilityStatus {
  const s = String(raw ?? "not_applicable");
  return BILLING_PROFITABILITY_STATUSES.includes(s as U3121BillingProjectProfitabilityStatus)
    ? (s as U3121BillingProjectProfitabilityStatus)
    : "not_applicable";
}

function parseBillingProjectProfitabilityByKind(raw: unknown): U3121BillingProjectProfitabilityByKind {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    transactionKind: String(r.transactionKind ?? r.kind ?? ""),
    lineCount: numOrZero(r.lineCount),
    totalCostAmount: numOrZero(r.totalCostAmount),
    totalSellingAmount: numOrZero(r.totalSellingAmount),
  };
}

function parseBillingProjectProfitability(raw: unknown): U3121BillingProjectProfitability {
  const r = (raw ?? {}) as Record<string, unknown>;
  const byKindRaw = Array.isArray(r.byKind) ? r.byKind : [];
  return {
    mode: parseBillingProjectProfitabilityMode(r.mode),
    status: parseBillingProjectProfitabilityStatus(r.status),
    referenceRevenue: numOrNull(r.referenceRevenue),
    actualCost: numOrZero(r.actualCost),
    actualRevenue: numOrNull(r.actualRevenue),
    profitOrMarginAmount: numOrNull(r.profitOrMarginAmount),
    profitOrMarginPercent: numOrNull(r.profitOrMarginPercent),
    multiplier: numOrNull(r.multiplier),
    costConsumedPercent: numOrNull(r.costConsumedPercent),
    byKind: byKindRaw.map(parseBillingProjectProfitabilityByKind),
  };
}

/** Parse `result` de GET /U3121/:id/billingSummary. */
export function parseU3121BillingSummaryFromApi(data: unknown): U3121BillingSummary | null {
  const root = data as { result?: unknown };
  const r = (root?.result ?? data) as Record<string, unknown> | null;
  if (!r || typeof r !== "object") return null;
  const canViewWip = Boolean(r.canViewWip);
  const canViewInvoices = Boolean(r.canViewInvoices);
  const canViewTimesheets = Boolean(r.canViewTimesheets);
  return {
    projectId: String(r.projectId ?? ""),
    billable: Boolean(r.billable),
    includeSubProjet: Boolean(r.includeSubProjet),
    canViewWip,
    canViewInvoices,
    canViewTimesheets,
    tec: canViewWip && r.tec != null ? parseBillingTecSummary(r.tec) : null,
    billingDocuments: canViewInvoices && r.billingDocuments != null ? parseBillingDocumentsSummary(r.billingDocuments) : null,
    unapprovedTimesheets: canViewTimesheets && r.unapprovedTimesheets != null ? parseBillingUnapprovedTimesheets(r.unapprovedTimesheets) : null,
    readinessHint: parseBillingReadinessHint(r.readinessHint),
    projectProfitability:
      canViewWip && r.projectProfitability != null ? parseBillingProjectProfitability(r.projectProfitability) : null,
  };
}

function parseBillingInvoiceConsumption(raw: unknown): U3121BillingInvoiceConsumption {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    targetInvoiceId: r.targetInvoiceId != null ? String(r.targetInvoiceId) : undefined,
    targetInvoiceNumber: (r.targetInvoiceNumber ?? r.target_invoice_number) as string | number | null | undefined,
    targetInvoiceDate: (r.targetInvoiceDate ?? r.target_invoice_date) as string | null | undefined,
    targetInvoiceStatus: (r.targetInvoiceStatus ?? r.target_invoice_status) as string | null | undefined,
    amount: r.amount != null ? numOrZero(r.amount) : undefined,
  };
}

export function parseU3121BillingInvoiceDetailLinesFromApi(data: unknown): U3121BillingInvoiceDetailLine[] {
  const root = data as { results?: unknown[]; result?: unknown };
  const list = Array.isArray(root?.results) ? root.results : Array.isArray(root?.result) ? root.result : [];
  return list.map((item) => {
    const r = (item ?? {}) as Record<string, unknown>;
    const consumptionsRaw = Array.isArray(r.consumptions) ? r.consumptions : [];
    const status = (r.status ?? r.invoiceStatus ?? r.invoice_status) as string | null | undefined;
    const docNature = (r.documentNature ?? r.document_nature) as U3121BillingInvoiceRowDocumentNature | undefined;
    return {
      id: String(r.id ?? ""),
      documentNature: docNature,
      invoiceNumber: (r.invoiceNumber ?? r.invoice_number) as string | number | null | undefined,
      invoiceDate: (r.invoiceDate ?? r.invoice_date) as string | null | undefined,
      status,
      invoiceStatus: status,
      invoiceTypeCode: String(r.invoiceTypeCode ?? r.invoice_type_code ?? ""),
      invoiceTypeLabelFr: String(r.invoiceTypeLabelFr ?? r.invoice_type_label_fr ?? r.invoiceTypeCode ?? r.invoice_type_code ?? ""),
      invoiceTypeLabelEn: String(r.invoiceTypeLabelEn ?? r.invoice_type_label_en ?? r.invoiceTypeLabelFr ?? r.invoice_type_label_fr ?? r.invoiceTypeCode ?? r.invoice_type_code ?? ""),
      amountTec: r.amountTec != null ? numOrZero(r.amountTec) : undefined,
      grossAmountInvoiceTaxable:
        r.grossAmountInvoiceTaxable != null
          ? numOrZero(r.grossAmountInvoiceTaxable)
          : r.amountInvoiceTaxable != null
            ? numOrZero(r.amountInvoiceTaxable)
            : undefined,
      grossAmountInvoiceNonTaxable:
        r.grossAmountInvoiceNonTaxable != null
          ? numOrZero(r.grossAmountInvoiceNonTaxable)
          : r.amountInvoiceNonTaxable != null
            ? numOrZero(r.amountInvoiceNonTaxable)
            : undefined,
      amountInvoiceTaxable: r.amountInvoiceTaxable != null ? numOrZero(r.amountInvoiceTaxable) : undefined,
      amountInvoiceNonTaxable: r.amountInvoiceNonTaxable != null ? numOrZero(r.amountInvoiceNonTaxable) : undefined,
      taxAmount1: r.taxAmount1 != null ? numOrZero(r.taxAmount1) : null,
      taxAmount2: r.taxAmount2 != null ? numOrZero(r.taxAmount2) : null,
      invoiceTotal: r.invoiceTotal != null ? numOrZero(r.invoiceTotal) : undefined,
      paymentsReceived: r.paymentsReceived != null ? numOrZero(r.paymentsReceived) : undefined,
      balanceDue: r.balanceDue != null ? numOrZero(r.balanceDue) : undefined,
      originalAmount: r.originalAmount != null ? numOrZero(r.originalAmount) : undefined,
      originalAmountTaxable:
        r.originalAmountTaxable != null
          ? numOrZero(r.originalAmountTaxable)
          : r.original_amount_taxable != null
            ? numOrZero(r.original_amount_taxable)
            : undefined,
      originalAmountNonTaxable:
        r.originalAmountNonTaxable != null
          ? numOrZero(r.originalAmountNonTaxable)
          : r.original_amount_non_taxable != null
            ? numOrZero(r.original_amount_non_taxable)
            : undefined,
      appliedAmount: r.appliedAmount != null ? numOrZero(r.appliedAmount) : undefined,
      appliedAmountTaxable:
        r.appliedAmountTaxable != null
          ? numOrZero(r.appliedAmountTaxable)
          : r.applied_amount_taxable != null
            ? numOrZero(r.applied_amount_taxable)
            : undefined,
      appliedAmountNonTaxable:
        r.appliedAmountNonTaxable != null
          ? numOrZero(r.appliedAmountNonTaxable)
          : r.applied_amount_non_taxable != null
            ? numOrZero(r.applied_amount_non_taxable)
            : undefined,
      remainingAmount: r.remainingAmount != null ? numOrZero(r.remainingAmount) : undefined,
      remainingAmountTaxable:
        r.remainingAmountTaxable != null
          ? numOrZero(r.remainingAmountTaxable)
          : r.remaining_amount_taxable != null
            ? numOrZero(r.remaining_amount_taxable)
            : undefined,
      remainingAmountNonTaxable:
        r.remainingAmountNonTaxable != null
          ? numOrZero(r.remainingAmountNonTaxable)
          : r.remaining_amount_non_taxable != null
            ? numOrZero(r.remaining_amount_non_taxable)
            : undefined,
      fullyConsumed: r.fullyConsumed != null ? Boolean(r.fullyConsumed) : undefined,
      consumptions: consumptionsRaw.map(parseBillingInvoiceConsumption),
    };
  });
}

export function parseU3121BillingTimesheetDetailLinesFromApi(data: unknown): U3121BillingTimesheetDetailLine[] {
  const root = data as { results?: unknown[]; result?: unknown };
  const list = Array.isArray(root?.results) ? root.results : Array.isArray(root?.result) ? root.result : [];
  return list.map((item) => {
    const r = (item ?? {}) as Record<string, unknown>;
    const emp = (r.employee ?? {}) as Record<string, unknown>;
    const periodDate = (r.periodDate ?? r.period_date ?? r.periodStart ?? r.period_start) as string | null | undefined;
    const totalReportedHours = numOrZero(r.totalReportedHours ?? r.reportedHours ?? r.projectHonorariumHours);
    const projectExpenseAmount = numOrZero(r.projectExpenseAmount ?? r.expenseAmount ?? r.totalExpenseAmount);
    const projectEquipmentAmount = numOrZero(r.projectEquipmentAmount ?? r.equipmentAmount ?? r.totalEquipmentAmount);
    return {
      id: String(r.id ?? ""),
      periodDate,
      status: (r.status ?? r.timesheetStatus) as string | null | undefined,
      employee: {
        id: emp.id != null ? String(emp.id) : undefined,
        codeEmployee: (emp.codeEmployee ?? r.employeeCode) as string | null | undefined,
        firstName: (emp.firstName ?? r.employeeFirstName) as string | null | undefined,
        lastName: (emp.lastName ?? r.employeeLastName) as string | null | undefined,
      },
      totalReportedHours,
      projectHonorariumHours: r.projectHonorariumHours != null ? numOrZero(r.projectHonorariumHours) : undefined,
      projectExpenseAmount,
      projectEquipmentAmount,
      employeeCode: (emp.codeEmployee ?? r.employeeCode) as string | null | undefined,
      employeeFirstName: (emp.firstName ?? r.employeeFirstName) as string | null | undefined,
      employeeLastName: (emp.lastName ?? r.employeeLastName) as string | null | undefined,
      periodStart: periodDate,
      reportedHours: totalReportedHours,
      expenseAmount: projectExpenseAmount,
      equipmentAmount: projectEquipmentAmount,
    };
  });
}

export function parseU3121BillingTecDetailLinesFromApi(data: unknown): U3121BillingTecDetailLine[] {
  const root = data as { results?: unknown[]; result?: unknown };
  const list = Array.isArray(root?.results) ? root.results : Array.isArray(root?.result) ? root.result : [];
  return list.map((item) => {
    const r = (item ?? {}) as Record<string, unknown>;
    const emp = (r.employee ?? {}) as Record<string, unknown>;
    const sp = (r.subProject ?? r.subproject) as Record<string, unknown> | undefined;
    const task = (r.task ?? {}) as Record<string, unknown>;
    const inv = (r.invoice ?? null) as Record<string, unknown> | null;
    return {
      id: String(r.id ?? ""),
      transactionKind: (r.transactionKind ?? r.transaction_kind) as U3121BillingTecDetailLine["transactionKind"],
      honorariumEntryKind: (r.honorariumEntryKind ?? r.honorarium_entry_kind) as string | null | undefined,
      transactionDate: (r.transactionDate ?? r.transaction_date) as string | null | undefined,
      quantity: r.quantity != null ? numOrZero(r.quantity) : undefined,
      amount: r.amount != null ? numOrZero(r.amount) : null,
      costAmount: r.costAmount != null ? numOrZero(r.costAmount) : null,
      sellingRate: r.sellingRate != null ? numOrZero(r.sellingRate) : r.selling_rate != null ? numOrZero(r.selling_rate) : null,
      costRate: r.costRate != null ? numOrZero(r.costRate) : r.cost_rate != null ? numOrZero(r.cost_rate) : null,
      sellingMarkupFactor:
        r.sellingMarkupFactor != null
          ? numOrZero(r.sellingMarkupFactor)
          : r.selling_markup_factor != null
            ? numOrZero(r.selling_markup_factor)
            : null,
      costMarkupFactor:
        r.costMarkupFactor != null
          ? numOrZero(r.costMarkupFactor)
          : r.cost_markup_factor != null
            ? numOrZero(r.cost_markup_factor)
            : null,
      billable: r.billable != null ? Boolean(r.billable) : undefined,
      hourly: r.hourly != null ? Boolean(r.hourly) : null,
      remark: (r.remark ?? null) as string | null | undefined,
      employee: emp.id != null || emp.codeEmployee != null || emp.firstName != null || emp.lastName != null
        ? {
            id: emp.id != null ? String(emp.id) : undefined,
            codeEmployee: emp.codeEmployee as string | null | undefined,
            firstName: emp.firstName as string | null | undefined,
            lastName: emp.lastName as string | null | undefined,
          }
        : null,
      task: task.id != null
        ? {
            id: String(task.id),
            taskCode: (task.taskCode ?? task.task_code) as string | null | undefined,
          }
        : null,
      subProject: sp
        ? {
            id: sp.id != null ? String(sp.id) : undefined,
            subProjetNumber: (sp.subProjetNumber ?? sp.sub_projet_number) as string | undefined,
            subProjetName: (sp.subProjetName ?? sp.sub_projet_name) as string | undefined,
          }
        : null,
      invoice: inv
        ? {
            id: inv.id != null ? String(inv.id) : undefined,
            invoiceNumber: (inv.invoiceNumber ?? inv.invoice_number) as string | null | undefined,
            documentNature: (inv.documentNature ?? inv.document_nature) as string | undefined,
            status: (inv.status ?? null) as string | undefined,
          }
        : null,
      expenseType: parseU3121BillingExpenseTypeRef(r.expenseType ?? r.expense_type),
      equipmentType: parseU3121BillingEquipmentTypeRef(r.equipmentType ?? r.equipment_type),
    };
  });
}

/**
 * Parse `GET …/billingSummary/details/tec` et normalise vers le modèle WIP (même chemin que U3125).
 */
export function parseU3121BillingTecDetailLinesToWipRows(data: unknown): U3134WorkInProgressSearchRow[] {
  return extractBillingSummaryResultsList(data).map((raw) =>
    normalizeU3125WipTransactionLine(prepareBillingTecDetailRawForWipNormalize(raw)),
  );
}

/** @deprecated Préférer `parseU3121BillingTecDetailLinesToWipRows` pour l’affichage tableau WIP. */
export function mapU3121BillingTecDetailLineToWipRow(line: U3121BillingTecDetailLine): U3134WorkInProgressSearchRow {
  return normalizeU3125WipTransactionLine(
    prepareBillingTecDetailRawForWipNormalize({
      id: line.id,
      transactionKind: line.transactionKind,
      honorariumEntryKind: line.honorariumEntryKind,
      transactionDate: line.transactionDate,
      quantity: line.quantity,
      billable: line.billable,
      hourly: line.hourly,
      costRate: line.costRate,
      costAmount: line.costAmount,
      sellingRate: line.sellingRate,
      sellingMarkupFactor: line.sellingMarkupFactor,
      costMarkupFactor: line.costMarkupFactor,
      amount: line.amount,
      remark: line.remark,
      employee: line.employee,
      task: line.task,
      subproject: line.subProject,
      invoice: line.invoice,
      expenseType: line.expenseType,
      equipmentType: line.equipmentType,
    }),
  );
}

export const doGetBillingSummary = (axios: AxiosInstance, projectId: string | number) =>
  axios.get(LogicalUnitEndpoints.BILLING_SUMMARY(projectId));

export const doGetBillingSummaryTecDetails = (
  axios: AxiosInstance,
  projectId: string | number,
  pool: U3121BillingTecPool,
) => axios.get(LogicalUnitEndpoints.BILLING_SUMMARY_TEC_DETAILS(projectId), { params: { pool } });

export const doGetBillingSummaryInvoiceDetails = (
  axios: AxiosInstance,
  projectId: string | number,
  documentNature: U3121BillingDocumentNature,
) => axios.get(LogicalUnitEndpoints.BILLING_SUMMARY_INVOICE_DETAILS(projectId), { params: { documentNature } });

export const doGetBillingSummaryTimesheetDetails = (axios: AxiosInstance, projectId: string | number) =>
  axios.get(LogicalUnitEndpoints.BILLING_SUMMARY_TIMESHEET_DETAILS(projectId));

export const doGetLandRegisterCoverage = (axios: AxiosInstance, projectId: string | number) =>
  axios.get(LogicalUnitEndpoints.LAND_REGISTER_COVERAGE(projectId));

export const doGetSubProjectLandRegisterCoverage = (axios: AxiosInstance, projectId: string | number, subProjectId: string | number) =>
  axios.get(LogicalUnitEndpoints.SUBPROJECT_LAND_REGISTER_COVERAGE(projectId, subProjectId));

export const doAdoptGoogleDueDate = (axios: AxiosInstance, projectId: string | number) =>
  axios.post(LogicalUnitEndpoints.ADOPT_GOOGLE_DUE_DATE(projectId));

export const doAdoptSubProjectGoogleDueDate = (
  axios: AxiosInstance,
  projectId: string | number,
  subProjectId: string | number,
) => axios.post(LogicalUnitEndpoints.SUBPROJECT_ADOPT_GOOGLE_DUE_DATE(projectId, subProjectId));

export function parseU3121AdoptGoogleDueDateResult(raw: unknown): U3121AdoptGoogleDueDateResult | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const dueDate = typeof r.dueDate === "string" ? r.dueDate.trim() : "";
  const version = r.version;
  if (!dueDate || typeof version !== "number" || Number.isNaN(version)) return null;
  return { dueDate, version };
}

export function luHandleAppiError(t: TranslateFn, nameSpace: string, err: unknown, errorResult: U2000ApiErrorResult) {
  const apiErr = err as { errorCode?: string; errorType?: string; description?: string };

  switch (apiErr?.errorCode) {
    case U3121_ERROR_PROJECT_NUMBER_DUPLICATE:
      errorResult.fieldErrors["projetNumber"] = t("errProjectNumberDuplicate", { ns: nameSpace });
      break;
    case U3121_ERROR_PROJECT_ID_MISMATCH:
      if (apiErr.description && /size|volumineux|large|too big/i.test(String(apiErr.description))) {
        errorResult.genericErrors.push({
          title: t("errorTitle", { ns: nameSpace }),
          text: t("errorBU312102FileSize", { ns: nameSpace }),
        });
      } else {
        errorResult.genericErrors.push({
          title: t("errorTitle", { ns: nameSpace }),
          text: t("errProjectIdMismatch", { ns: nameSpace }),
        });
      }
      break;
    case U3121_ERROR_SUBPROJECT_NUMBER_DUPLICATE:
      if (apiErr.errorType === U3121_ERROR_TYPE_EBA_VALIDATION) {
        errorResult.fieldErrors["hourRate"] = t("errHourRateRequired", { ns: nameSpace });
      } else {
        errorResult.fieldErrors["subProjetNumber"] = t("errSubProjectNumberDuplicate", { ns: nameSpace });
      }
      break;
    case U3121_ERROR_EBA_VALIDFROM_REQUIRED:
      if (apiErr.description && /file|manquant|missing|champ/i.test(String(apiErr.description))) {
        errorResult.genericErrors.push({
          title: t("errorTitle", { ns: nameSpace }),
          text: t("errorBU312104", { ns: nameSpace }),
        });
      } else {
        errorResult.fieldErrors["validFrom"] = t("errValidFromRequired", { ns: nameSpace });
      }
      break;
    case U3121_ERROR_UPLOAD_INVALID_FILE_TYPE:
      errorResult.genericErrors.push({
        title: t("errorTitle", { ns: nameSpace }),
        text: t("errorBU312105", { ns: nameSpace }),
      });
      break;
    case U3121_ERROR_UPLOAD_DEFAULT_COUNTRY_STATE:
      errorResult.genericErrors.push({
        title: t("errorTitle", { ns: nameSpace }),
        text: t("errorBU312106", { ns: nameSpace }),
      });
      break;
    case U3121_ERROR_UPLOAD_INVALID_BUDGET:
      errorResult.genericErrors.push({
        title: t("errorTitle", { ns: nameSpace }),
        text: t("errorBU312107", { ns: nameSpace }),
      });
      break;
    case "BU315203":
      errorResult.fieldErrors["contactType.id"] = t("errContactTypeInactive", { ns: nameSpace });
      break;
    case "BU320101":
      errorResult.genericErrors.push({
        title: t("errorTitle", { ns: nameSpace }),
        text: t("errorBU320101", { ns: nameSpace }),
      });
      break;
    case "BU320106":
      errorResult.genericErrors.push({
        title: t("errorTitle", { ns: nameSpace }),
        text: t("errorBU320106", { ns: nameSpace }),
      });
      break;
    case "BU320107":
      errorResult.genericErrors.push({
        title: t("errorTitle", { ns: nameSpace }),
        text: t("errorBU320107", { ns: nameSpace }),
      });
      break;
    case "BU320108":
      errorResult.genericErrors.push({
        title: t("errorTitle", { ns: nameSpace }),
        text: t("errorBU320108", { ns: nameSpace }),
      });
      break;
    default:
      break;
  }
}
